import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Input } from '@/components/ui/input';
import { Search, Filter, Calendar, User, Clock, CheckCircle2, TrendingUp, AlertCircle, Edit, Trash2, Plus, MessageSquare } from 'lucide-react';
import { Textarea } from '@/components/ui/textarea';
import CommentThread from '@/components/CommentThread';
import './styles/TasksOverview.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function TasksOverview({ user }) {
  const [tasks, setTasks] = useState([]);
  const [teams, setTeams] = useState([]);
  const [selectedTeam, setSelectedTeam] = useState('all');
  const [teamMembers, setTeamMembers] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [assigneeFilter, setAssigneeFilter] = useState('all');
  const [sortBy, setSortBy] = useState('recent');
  const [selectedTask, setSelectedTask] = useState(null);
  const [isDetailsOpen, setIsDetailsOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editedTask, setEditedTask] = useState({ title: '', description: '', status: '' });
  const [isEditingDetails, setIsEditingDetails] = useState(false);
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [newTask, setNewTask] = useState({ title: '', description: '', assigned_to: '', team_id: '' });
  const isUpdatingRef = useRef(false);

  useEffect(() => {
    fetchTeams();
    fetchTasks();
  }, []);

  useEffect(() => {
    fetchTasks();
    fetchTeamMembers();
  }, [selectedTeam]);

  // Fetch team members for the selected team in create dialog
  useEffect(() => {
    if (isCreateDialogOpen && newTask.team_id) {
      const fetchDialogTeamMembers = async () => {
        try {
          const token = localStorage.getItem('session_token');
          const response = await axios.get(`${API}/teams/${newTask.team_id}/members`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          setTeamMembers(response.data.filter(m => !m.is_pending));
        } catch (error) {
          console.error('Failed to fetch team members for dialog');
        }
      };
      fetchDialogTeamMembers();
    }
  }, [isCreateDialogOpen, newTask.team_id]);

  // Listen for task updates from other components
  useEffect(() => {
    const handleStorageChange = (e) => {
      if (e.key === 'tasks_updated' && !isUpdatingRef.current) {
        console.log('TasksOverview: Refetching tasks due to storage change');
        // Fetch tasks regardless of current state
        const token = localStorage.getItem('session_token');
        const url = selectedTeam === 'all' ? `${API}/tasks` : `${API}/tasks?team_id=${selectedTeam}`;
        axios.get(url, {
          headers: { Authorization: `Bearer ${token}` }
        }).then(response => {
          setTasks(response.data);
        }).catch(error => {
          console.error('Failed to fetch tasks:', error);
        });
      }
    };
    
    window.addEventListener('storage', handleStorageChange);
    
    // Also listen to custom event for same-window updates
    const handleTaskUpdate = () => {
      if (!isUpdatingRef.current) {
        console.log('TasksOverview: Refetching tasks due to custom event');
        // Fetch tasks regardless of current state
        const token = localStorage.getItem('session_token');
        const url = selectedTeam === 'all' ? `${API}/tasks` : `${API}/tasks?team_id=${selectedTeam}`;
        axios.get(url, {
          headers: { Authorization: `Bearer ${token}` }
        }).then(response => {
          setTasks(response.data);
        }).catch(error => {
          console.error('Failed to fetch tasks:', error);
        });
      }
    };
    
    window.addEventListener('taskUpdated', handleTaskUpdate);
    
    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('taskUpdated', handleTaskUpdate);
    };
  }, [selectedTeam]);

  const fetchTeams = async () => {
    try {
      const token = localStorage.getItem('session_token');
      const response = await axios.get(`${API}/teams`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTeams(response.data);
    } catch (error) {
      toast.error('Failed to fetch teams');
    }
  };

  const fetchTasks = async () => {
    try {
      const token = localStorage.getItem('session_token');
      const url = selectedTeam === 'all' ? `${API}/tasks` : `${API}/tasks?team_id=${selectedTeam}`;
      const response = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTasks(response.data);
    } catch (error) {
      toast.error('Failed to fetch tasks');
    }
  };

  const fetchTeamMembers = async () => {
    try {
      const token = localStorage.getItem('session_token');
      
      if (selectedTeam === 'all') {
        // Fetch all teams to get members from all user's teams
        const teamsResponse = await axios.get(`${API}/teams`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        // Collect unique members from all teams
        const allMembers = [];
        const memberIds = new Set();
        
        for (const team of teamsResponse.data) {
          const membersResponse = await axios.get(`${API}/teams/${team.id}/members`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          
          membersResponse.data.forEach(member => {
            if (!member.is_pending && !memberIds.has(member.id)) {
              memberIds.add(member.id);
              allMembers.push(member);
            }
          });
        }
        
        setTeamMembers(allMembers);
      } else {
        // Fetch team-specific members
        const response = await axios.get(`${API}/teams/${selectedTeam}/members`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setTeamMembers(response.data.filter(m => !m.is_pending));
      }
    } catch (error) {
      console.error('Failed to fetch team members');
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'todo': return 'status-todo';
      case 'in-progress': return 'status-in-progress';
      case 'done': return 'status-done';
      default: return '';
    }
  };

  const getMemberName = (memberId) => {
    if (!memberId) return 'Unassigned';
    const member = teamMembers.find(m => m.id === memberId);
    return member ? member.name : 'Unknown';
  };

  const getTaskStats = () => {
    return {
      total: tasks.length,
      completed: tasks.filter(t => t.status === 'done').length,
      inProgress: tasks.filter(t => t.status === 'in-progress').length,
      todo: tasks.filter(t => t.status === 'todo').length,
      myTasks: tasks.filter(t => t.assigned_to === user.id).length
    };
  };

  const filterAndSortTasks = () => {
    let filtered = tasks.filter(task => {
      const matchesSearch = task.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
                           task.description.toLowerCase().includes(searchQuery.toLowerCase());
      const matchesStatus = statusFilter === 'all' || task.status === statusFilter;
      const matchesAssignee = assigneeFilter === 'all' || 
                             (assigneeFilter === 'unassigned' && !task.assigned_to) ||
                             (assigneeFilter === 'mine' && task.assigned_to === user.id) ||
                             task.assigned_to === assigneeFilter;
      return matchesSearch && matchesStatus && matchesAssignee;
    });

    // Sort
    switch (sortBy) {
      case 'recent':
        filtered.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
        break;
      case 'oldest':
        filtered.sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
        break;
      case 'updated':
        filtered.sort((a, b) => new Date(b.updated_at) - new Date(a.updated_at));
        break;
      case 'title':
        filtered.sort((a, b) => a.title.localeCompare(b.title));
        break;
      default:
        break;
    }

    return filtered;
  };

  const openTaskDetails = (task) => {
    setSelectedTask(task);
    setEditedTask({ title: task.title, description: task.description, status: task.status });
    setIsDetailsOpen(true);
    setIsEditing(false);
    setIsEditingDetails(false);
  };

  const updateTaskDetails = async () => {
    try {
      isUpdatingRef.current = true;
      const token = localStorage.getItem('session_token');
      
      // Update task title and description in backend
      const updatedTask = {
        ...selectedTask,
        title: editedTask.title,
        description: editedTask.description
      };
      
      await axios.put(`${API}/tasks/${selectedTask.id}`, updatedTask, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Task details updated successfully');
      setIsEditingDetails(false);
      fetchTasks();
      setIsDetailsOpen(false);
      
      // Notify other components
      localStorage.setItem('tasks_updated', Date.now().toString());
      window.dispatchEvent(new Event('taskUpdated'));
      
      setTimeout(() => {
        isUpdatingRef.current = false;
      }, 500);
    } catch (error) {
      toast.error('Failed to update task details');
      isUpdatingRef.current = false;
    }
  };

  const reassignTask = async (taskId, newAssignee) => {
    // Check if assignment actually changed
    const currentTask = selectedTask;
    const currentAssignee = currentTask.assigned_to || 'unassigned';
    const newAssigneeValue = newAssignee || 'unassigned';
    
    if (currentAssignee === newAssigneeValue) {
      console.log('Assignment unchanged, skipping update');
      return;
    }
    
    try {
      isUpdatingRef.current = true;
      const token = localStorage.getItem('session_token');
      await axios.patch(`${API}/tasks/${taskId}`, 
        { assigned_to: newAssignee },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Task reassigned successfully');
      await fetchTasks();
      
      // Notify other components
      console.log('TasksOverview: Dispatching task update event');
      localStorage.setItem('tasks_updated', Date.now().toString());
      window.dispatchEvent(new Event('taskUpdated'));
      
      setTimeout(() => {
        isUpdatingRef.current = false;
      }, 500);
    } catch (error) {
      toast.error('Failed to reassign task');
      isUpdatingRef.current = false;
    }
  };

  const updateTask = async () => {
    try {
      isUpdatingRef.current = true;
      const token = localStorage.getItem('session_token');
      await axios.patch(`${API}/tasks/${selectedTask.id}`, 
        { 
          status: editedTask.status
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Task updated successfully');
      setIsEditing(false);
      await fetchTasks();
      setIsDetailsOpen(false);
      
      // Notify other components
      console.log('TasksOverview: Dispatching task update event');
      localStorage.setItem('tasks_updated', Date.now().toString());
      window.dispatchEvent(new Event('taskUpdated'));
      
      setTimeout(() => {
        isUpdatingRef.current = false;
      }, 500);
    } catch (error) {
      toast.error('Failed to update task');
      isUpdatingRef.current = false;
    }
  };

  const deleteTask = async (taskId) => {
    if (!window.confirm('Are you sure you want to delete this task?')) {
      return;
    }
    
    try {
      isUpdatingRef.current = true;
      const token = localStorage.getItem('session_token');
      await axios.delete(`${API}/tasks/${taskId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Task deleted successfully');
      await fetchTasks();
      setIsDetailsOpen(false);
      
      // Notify other components
      console.log('TasksOverview: Dispatching task update event');
      localStorage.setItem('tasks_updated', Date.now().toString());
      window.dispatchEvent(new Event('taskUpdated'));
      
      setTimeout(() => {
        isUpdatingRef.current = false;
      }, 500);
    } catch (error) {
      toast.error('Failed to delete task');
      isUpdatingRef.current = false;
    }
  };


  const createTask = async () => {
    if (!newTask.title || !newTask.team_id) {
      toast.error('Please fill in task title and select a team');
      return;
    }

    try {
      const token = localStorage.getItem('session_token');
      // Handle assigned_to: convert empty string or 'unassigned' to null
      const assignedTo = newTask.assigned_to && newTask.assigned_to !== 'unassigned' ? newTask.assigned_to : null;
      
      const response = await axios.post(`${API}/tasks`, {
        title: newTask.title,
        description: newTask.description,
        team_id: newTask.team_id,
        assigned_to: assignedTo
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      console.log('Task created:', response.data);
      toast.success('Task created successfully');
      setIsCreateDialogOpen(false);
      setNewTask({ title: '', description: '', assigned_to: '', team_id: '' });
      await fetchTasks();
      
      // Notify other components
      localStorage.setItem('tasks_updated', Date.now().toString());
      window.dispatchEvent(new Event('taskUpdated'));
    } catch (error) {
      console.error('Failed to create task:', error);
      toast.error(error.response?.data?.detail || 'Failed to create task');
    }
  };


  const stats = getTaskStats();
  const filteredTasks = filterAndSortTasks();
  const tasksByStatus = {
    todo: filteredTasks.filter(t => t.status === 'todo'),
    'in-progress': filteredTasks.filter(t => t.status === 'in-progress'),
    done: filteredTasks.filter(t => t.status === 'done')
  };

  return (
    <div className="tasks-overview" data-testid="tasks-overview">
      <div className="page-header">
        <div>
          <h1 className="page-title">Current Tasks</h1>
          <p className="page-subtitle">View and manage all your tasks</p>
        </div>
        <div style={{ display: 'flex', gap: '1rem', alignItems: 'center' }}>
          <Button 
            onClick={() => setIsCreateDialogOpen(true)}
            style={{ 
              background: 'linear-gradient(135deg, #1a936f 0%, #0f6b4f 100%)',
              color: 'white',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              border: 'none'
            }}
          >
            <Plus size={20} />
            Create Task
          </Button>
          <Select value={selectedTeam} onValueChange={setSelectedTeam}>
            <SelectTrigger className="w-[200px]" data-testid="team-filter">
              <SelectValue placeholder="Filter by team" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Teams</SelectItem>
              {teams.map(team => (
                <SelectItem key={team.id} value={team.id}>{team.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Stats Dashboard */}
      <div className="tasks-stats">
        <Card className="stat-card">
          <div className="stat-icon" style={{ background: 'linear-gradient(135deg, #1a936f 0%, #0f6b4f 100%)' }}>
            <CheckCircle2 size={24} />
          </div>
          <div className="stat-content">
            <h3>{stats.total}</h3>
            <p>Total Tasks</p>
          </div>
        </Card>
        <Card className="stat-card">
          <div className="stat-icon" style={{ background: 'linear-gradient(135deg, #26a17b 0%, #1a936f 100%)' }}>
            <Clock size={24} />
          </div>
          <div className="stat-content">
            <h3>{stats.inProgress}</h3>
            <p>In Progress</p>
          </div>
        </Card>
        <Card className="stat-card">
          <div className="stat-icon" style={{ background: 'linear-gradient(135deg, #33b087 0%, #26a17b 100%)' }}>
            <AlertCircle size={24} />
          </div>
          <div className="stat-content">
            <h3>{stats.todo}</h3>
            <p>To Do</p>
          </div>
        </Card>
        <Card className="stat-card">
          <div className="stat-icon" style={{ background: 'linear-gradient(135deg, #40bf93 0%, #33b087 100%)' }}>
            <User size={24} />
          </div>
          <div className="stat-content">
            <h3>{stats.myTasks}</h3>
            <p>My Tasks</p>
          </div>
        </Card>
      </div>

      {/* Filters Bar */}
      <div className="filters-section">
        <div className="search-box">
          <Search size={20} />
          <Input
            placeholder="Search tasks..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="search-input"
          />
        </div>
        <div className="filter-controls">
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-[150px]">
              <SelectValue placeholder="Status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Status</SelectItem>
              <SelectItem value="todo">To Do</SelectItem>
              <SelectItem value="in-progress">In Progress</SelectItem>
              <SelectItem value="done">Done</SelectItem>
            </SelectContent>
          </Select>
          
          <Select value={assigneeFilter} onValueChange={setAssigneeFilter}>
            <SelectTrigger className="w-[150px]">
              <SelectValue placeholder="Assignee" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Members</SelectItem>
              <SelectItem value="mine">My Tasks</SelectItem>
              <SelectItem value="unassigned">Unassigned</SelectItem>
              {teamMembers.map(member => (
                <SelectItem key={member.id} value={member.id}>{member.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          
          <Select value={sortBy} onValueChange={setSortBy}>
            <SelectTrigger className="w-[150px]">
              <SelectValue placeholder="Sort by" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="recent">Most Recent</SelectItem>
              <SelectItem value="oldest">Oldest First</SelectItem>
              <SelectItem value="updated">Last Updated</SelectItem>
              <SelectItem value="title">Title A-Z</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      <Tabs defaultValue="list" className="tasks-tabs">
        <TabsList>
          <TabsTrigger value="list">List View</TabsTrigger>
          <TabsTrigger value="grouped">Grouped by Status</TabsTrigger>
          <TabsTrigger value="timeline">Timeline</TabsTrigger>
        </TabsList>

        <TabsContent value="list" className="tab-content">
          <div className="tasks-list">
            {filteredTasks.length === 0 ? (
              <div className="empty-state">
                <AlertCircle size={48} />
                <p>No tasks found</p>
              </div>
            ) : (
              filteredTasks.map(task => (
                <Card 
                  key={task.id} 
                  className="task-item"
                  onClick={() => openTaskDetails(task)}
                  data-testid={`task-item-${task.id}`}
                >
                  <div className="task-header">
                    <h3>{task.title}</h3>
                    <Badge className={getStatusColor(task.status)} data-testid={`task-status-${task.id}`}>
                      {task.status.replace('-', ' ')}
                    </Badge>
                  </div>
                  <p className="task-description">{task.description}</p>
                  <div className="task-footer">
                    <div className="task-meta">
                      <span className="assigned-to">
                        <User size={14} />
                        {getMemberName(task.assigned_to)}
                      </span>
                      <span className="task-date">
                        <Calendar size={14} />
                        {new Date(task.created_at).toLocaleDateString()}
                      </span>
                    </div>
                  </div>
                </Card>
              ))
            )}
          </div>
        </TabsContent>

        <TabsContent value="grouped" className="tab-content">
          <div className="grouped-tasks">
            {Object.entries(tasksByStatus).map(([status, statusTasks]) => (
              <div key={status} className="status-group">
                <div className="status-group-header">
                  <h3>{status.replace('-', ' ').toUpperCase()}</h3>
                  <Badge variant="secondary">{statusTasks.length}</Badge>
                </div>
                <div className="status-group-tasks">
                  {statusTasks.map(task => (
                    <Card 
                      key={task.id} 
                      className="grouped-task-card"
                      onClick={() => openTaskDetails(task)}
                    >
                      <h4>{task.title}</h4>
                      <p>{task.description}</p>
                      <div className="task-assignee-info">
                        <User size={14} />
                        <span>{getMemberName(task.assigned_to)}</span>
                      </div>
                    </Card>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="timeline" className="tab-content">
          <div className="timeline-view">
            {filteredTasks.map((task, index) => (
              <div key={task.id} className="timeline-item">
                <div className="timeline-marker">
                  <div className={`timeline-dot ${getStatusColor(task.status)}`} />
                  {index < filteredTasks.length - 1 && <div className="timeline-line" />}
                </div>
                <Card className="timeline-card" onClick={() => openTaskDetails(task)}>
                  <div className="timeline-card-header">
                    <h4>{task.title}</h4>
                    <Badge className={getStatusColor(task.status)}>
                      {task.status.replace('-', ' ')}
                    </Badge>
                  </div>
                  <p className="timeline-description">{task.description}</p>
                  <div className="timeline-meta">
                    <span>
                      <User size={14} />
                      {getMemberName(task.assigned_to)}
                    </span>
                    <span>
                      <Clock size={14} />
                      {new Date(task.created_at).toLocaleString()}
                    </span>
                  </div>
                </Card>
              </div>
            ))}
          </div>
        </TabsContent>
      </Tabs>

      {/* Task Details Dialog */}
      {selectedTask && (
        <Dialog open={isDetailsOpen} onOpenChange={setIsDetailsOpen}>
          <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="task-details-dialog">
            <DialogHeader>
              <DialogTitle>Task Details</DialogTitle>
            </DialogHeader>
            
            <Tabs defaultValue="details" className="w-full">
              <TabsList className="grid w-full grid-cols-2">
                <TabsTrigger value="details">Details</TabsTrigger>
                <TabsTrigger value="comments">
                  <MessageSquare size={16} className="mr-2" />
                  Comments
                </TabsTrigger>
              </TabsList>
              
              <TabsContent value="details" className="task-details-content">
              <div className="detail-row">
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <h4>Title</h4>
                  {!isEditingDetails && (
                    <Button 
                      variant="ghost" 
                      size="sm"
                      onClick={() => setIsEditingDetails(true)}
                      style={{ padding: '0.25rem' }}
                    >
                      <Edit size={16} />
                    </Button>
                  )}
                </div>
                {isEditingDetails ? (
                  <Input
                    value={editedTask.title}
                    onChange={(e) => setEditedTask({ ...editedTask, title: e.target.value })}
                    placeholder="Task title"
                  />
                ) : (
                  <p>{selectedTask.title}</p>
                )}
              </div>
              <div className="detail-row">
                <h4>Description</h4>
                {isEditingDetails ? (
                  <Textarea
                    value={editedTask.description}
                    onChange={(e) => setEditedTask({ ...editedTask, description: e.target.value })}
                    placeholder="Task description"
                    rows={4}
                  />
                ) : (
                  <p>{selectedTask.description}</p>
                )}
              </div>
              <div className="detail-row">
                <h4>Status</h4>
                {isEditing ? (
                  <Select 
                    value={editedTask.status}
                    onValueChange={(value) => setEditedTask({ ...editedTask, status: value })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="todo">To Do</SelectItem>
                      <SelectItem value="in-progress">In Progress</SelectItem>
                      <SelectItem value="done">Done</SelectItem>
                    </SelectContent>
                  </Select>
                ) : (
                  <Badge className={getStatusColor(selectedTask.status)}>
                    {selectedTask.status.replace('-', ' ')}
                  </Badge>
                )}
              </div>
              <div className="detail-row">
                <h4>Reassign To</h4>
                <Select 
                  value={selectedTask.assigned_to || 'unassigned'}
                  onValueChange={(value) => reassignTask(selectedTask.id, value === 'unassigned' ? null : value)}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="unassigned">Unassigned</SelectItem>
                    {teamMembers
                      .filter(member => {
                        // For "All Teams" view, show all members
                        // For team-specific view, members are already filtered
                        return true;
                      })
                      .map(member => (
                        <SelectItem key={member.id} value={member.id}>{member.name}</SelectItem>
                      ))
                    }
                  </SelectContent>
                </Select>
              </div>
              <div className="detail-row">
                <h4>Created</h4>
                <p>{new Date(selectedTask.created_at).toLocaleString()}</p>
              </div>
              <div className="detail-row">
                <h4>Last Updated</h4>
                <p>{new Date(selectedTask.updated_at).toLocaleString()}</p>
              </div>
              <div className="detail-row" style={{ marginTop: '1.5rem', display: 'flex', gap: '0.5rem' }}>
                {isEditingDetails ? (
                  <>
                    <Button onClick={updateTaskDetails} style={{ flex: 1 }}>
                      Save Details
                    </Button>
                    <Button variant="outline" onClick={() => {
                      setIsEditingDetails(false);
                      setEditedTask({ title: selectedTask.title, description: selectedTask.description, status: selectedTask.status });
                    }} style={{ flex: 1 }}>
                      Cancel
                    </Button>
                  </>
                ) : isEditing ? (
                  <>
                    <Button onClick={updateTask} style={{ flex: 1 }}>
                      Save Status
                    </Button>
                    <Button variant="outline" onClick={() => setIsEditing(false)} style={{ flex: 1 }}>
                      Cancel
                    </Button>
                  </>
                ) : (
                  <>
                    <Button variant="outline" onClick={() => setIsEditing(true)} style={{ flex: 1 }}>
                      <Edit size={16} style={{ marginRight: '0.5rem' }} />
                      Edit Status
                    </Button>
                    <Button variant="destructive" onClick={() => deleteTask(selectedTask.id)} style={{ flex: 1 }}>
                      <Trash2 size={16} style={{ marginRight: '0.5rem' }} />
                      Delete
                    </Button>
                  </>
                )}
              </div>
            </TabsContent>
            
            <TabsContent value="comments" className="mt-4">
              {selectedTask && (
                <CommentThread 
                  taskId={selectedTask.id} 
                  currentUser={user}
                />
              )}
            </TabsContent>
          </Tabs>
          </DialogContent>
        </Dialog>
      )}

      {/* Create Task Dialog */}
      <Dialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create New Task</DialogTitle>
          </DialogHeader>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
            <div>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 500 }}>Title *</label>
              <Input
                placeholder="Task title"
                value={newTask.title}
                onChange={(e) => setNewTask({ ...newTask, title: e.target.value })}
              />
            </div>
            <div>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 500 }}>Description</label>
              <Textarea
                placeholder="Task description"
                value={newTask.description}
                onChange={(e) => setNewTask({ ...newTask, description: e.target.value })}
                rows={4}
              />
            </div>
            <div>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 500 }}>Team *</label>
              <Select value={newTask.team_id} onValueChange={(value) => setNewTask({ ...newTask, team_id: value })}>
                <SelectTrigger>
                  <SelectValue placeholder="Select team" />
                </SelectTrigger>
                <SelectContent>
                  {teams.map(team => (
                    <SelectItem key={team.id} value={team.id}>{team.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 500 }}>Assign To</label>
              <Select value={newTask.assigned_to || 'unassigned'} onValueChange={(value) => setNewTask({ ...newTask, assigned_to: value === 'unassigned' ? '' : value })}>
                <SelectTrigger>
                  <SelectValue placeholder="Select assignee (optional)" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="unassigned">Unassigned</SelectItem>
                  {teamMembers
                    .filter(member => !member.is_pending)
                    .map(member => (
                      <SelectItem key={member.id} value={member.id}>{member.name}</SelectItem>
                    ))}
                </SelectContent>
              </Select>
            </div>
            <div style={{ display: 'flex', gap: '0.5rem', marginTop: '1rem' }}>
              <Button onClick={createTask} style={{ flex: 1 }}>Create Task</Button>
              <Button variant="outline" onClick={() => setIsCreateDialogOpen(false)} style={{ flex: 1 }}>Cancel</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
